import { useCallback, useEffect, useRef, useState } from "react";
import { PresentationDraft } from "../models/PresentationDraft";
import { useUpdatePresentationDraftContent } from "./usePresentationDrafts";
import { MAX_CONTENT_LENGTH } from "../utils/presentationDraft";

const AUTOSAVE_DELAY_MS = 1500;

interface PendingEdit {
	draftId: number;
	content: string;
}

interface LocalContent {
	draftKey: string;
	content: string;
}

export const presentationDraftKey = (draft: PresentationDraft | undefined): string =>
	draft ? `${draft.id}-${draft.status}` : "";

export const usePresentationDraftAutosave = (draft: PresentationDraft | undefined) => {
	const updateContent = useUpdatePresentationDraftContent();
	const { mutateAsync } = updateContent;
	const draftKey = presentationDraftKey(draft);
	const [localContent, setLocalContent] = useState<LocalContent>();
	const [savedAt, setSavedAt] = useState<Date>();
	const [hasSaveFailed, setHasSaveFailed] = useState(false);
	const pendingEdit = useRef<PendingEdit | null>(null);
	const timer = useRef<ReturnType<typeof setTimeout>>();

	const content = localContent?.draftKey === draftKey ? localContent.content : draft?.content ?? "";

	const saveQueue = useRef<Promise<boolean>>(Promise.resolve(true));

	const savePendingEdit = useCallback(async (): Promise<boolean> => {
		const edit = pendingEdit.current;
		if (!edit) return true;

		pendingEdit.current = null;
		try {
			await mutateAsync(edit);
			setSavedAt(new Date());
			setHasSaveFailed(false);
			return true;
		} catch {
			if (!pendingEdit.current) {
				pendingEdit.current = edit;
			}
			setHasSaveFailed(true);
			return false;
		}
	}, [mutateAsync]);

	// Saves are chained so an older version can never land on the server after a newer one.
	const flush = useCallback((): Promise<boolean> => {
		clearTimeout(timer.current);
		saveQueue.current = saveQueue.current.then(savePendingEdit);
		return saveQueue.current;
	}, [savePendingEdit]);

	useEffect(() => {
		setSavedAt(undefined);
		setHasSaveFailed(false);

		return () => {
			void flush();
		};
	}, [draftKey, flush]);

	const edit = useCallback(
		(value: string) => {
			if (!draft) return;

			setLocalContent({ draftKey: presentationDraftKey(draft), content: value });
			clearTimeout(timer.current);

			if (value.length > MAX_CONTENT_LENGTH) {
				pendingEdit.current = null;
				return;
			}

			pendingEdit.current = { draftId: draft.id, content: value };
			timer.current = setTimeout(() => void flush(), AUTOSAVE_DELAY_MS);
		},
		[draft, flush]
	);

	return {
		content,
		edit,
		flush,
		savedAt,
		hasSaveFailed,
		isSaving: updateContent.isPending,
		isTooLong: content.length > MAX_CONTENT_LENGTH
	};
};
